<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import WalletBalanceCard from './WalletBalanceCard.vue'
import ActivityList from '../../transactions/components/ActivityList.vue'
import AddFundsModal from './AddFundsModal.vue'
import WithdrawFundsModal from './WithdrawFundsModal.vue'
import { canTransactInWallet } from '../utils/walletAccessUtils'
import type { Transaction } from '../../transactions/types/transactionTypes'
import type { SlicealAccount } from '../types/fiatWalletTypes'
import type { BankAccount, KycActionState } from '@modules/account'

interface Props {
  /** Account access restricted (blocked investor) — hard-blocks both modals. */
  restricted?: boolean
  /** KYC gate. Only 'verified' (or 'in-review' with a verified bank) unlocks money moves. */
  kycState?: KycActionState
  currency?: string
  /** Formatted balance for display, e.g. `"50,745.00"`. */
  balance?: string
  /** Numeric balance — the withdraw cap and remaining-balance maths. */
  balanceAmount?: number
  lastUpdated?: string
  walletLoading?: boolean
  walletRefreshing?: boolean
  walletHasError?: boolean
  bankAccounts?: BankAccount[]
  defaultBankAccount?: BankAccount | null
  slicealAccount?: SlicealAccount | null
  activityTransactions?: Transaction[]
  activityLoading?: boolean
  activityLoadingMore?: boolean
  activityHasMore?: boolean
  withdrawing?: boolean
  depositSubmitting?: boolean
  depositError?: string | null
  /** Set by the container once the deposit is accepted — advances the modal to its receipt step. */
  depositSubmittedAt?: string
  /** Reference the deposit was filed under, shown on the receipt. */
  depositReference?: string
}

const props = withDefaults(defineProps<Props>(), {
  restricted: false,
  kycState: 'verified',
  currency: 'AED',
  balance: '',
  balanceAmount: 0,
  lastUpdated: '',
  walletLoading: false,
  walletRefreshing: false,
  walletHasError: false,
  bankAccounts: () => [],
  defaultBankAccount: null,
  slicealAccount: null,
  activityTransactions: () => [],
  activityLoading: false,
  activityLoadingMore: false,
  activityHasMore: false,
  withdrawing: false,
  depositSubmitting: false,
  depositError: null,
  depositSubmittedAt: '',
  depositReference: '',
})

const emit = defineEmits<{
  (e: 'deposit-confirm', payload: { amount: number; attachment: File }): void
  (e: 'deposit-clear-error'): void
  (e: 'deposit-view-history'): void
  (e: 'withdraw', payload: { amount: number; bankAccountId: string }): void
  (e: 'add-bank'): void
  (e: 'view-details', tx: Transaction): void
  (e: 'retry-wallet'): void
  (e: 'load-more'): void
}>()

const depositOpen = ref(false)
const withdrawOpen = ref(false)

const hasBankAccount = computed(() => props.bankAccounts.length > 0)
const hasActiveBank = computed(() =>
  props.bankAccounts.some((account) => account.accountStatus === 'verified'),
)

// Same gate the balance card's buttons use — re-checked here so a deep link or
// a stale prop can never open a modal the investor isn't allowed to submit.
const actionsBlocked = computed(
  () =>
    !canTransactInWallet({
      restricted: props.restricted,
      kycState: props.kycState,
      hasBankAccount: hasBankAccount.value,
      hasActiveBank: hasActiveBank.value,
    }),
)

const openDeposit = () => {
  if (actionsBlocked.value) return
  depositOpen.value = true
}

const openWithdraw = () => {
  if (actionsBlocked.value) return
  withdrawOpen.value = true
}

// The container owns the result modals; once the submission settles (either
// way) tear the withdraw dialog down so its outcome is the only thing on screen.
watch(
  () => props.withdrawing,
  (now, prev) => {
    if (prev && !now) withdrawOpen.value = false
  },
)

defineExpose({
  openDeposit,
  closeDeposit: () => {
    depositOpen.value = false
  },
})
</script>

<template>
  <Box direction="col" gap="6" class="fwt">
    <WalletBalanceCard
      :currency="currency"
      :balance="balance"
      :last-updated="lastUpdated"
      :loading="walletLoading"
      :refreshing="walletRefreshing"
      :has-error="walletHasError"
      :restricted="restricted"
      :kyc-state="kycState"
      :has-bank-account="hasBankAccount"
      :has-active-bank="hasActiveBank"
      @add-funds="openDeposit"
      @withdraw="openWithdraw"
      @add-bank="emit('add-bank')"
      @refresh="emit('retry-wallet')"
    />

    <ActivityList
      :transactions="activityTransactions"
      :loading="activityLoading"
      :loading-more="activityLoadingMore"
      :has-more="activityHasMore"
      :see-all-to="routes.WALLET_TRANSACTIONS"
      @load-more="emit('load-more')"
      @view-details="(tx) => emit('view-details', tx)"
      @add-funds="openDeposit"
    />

    <AddFundsModal
      v-model:visible="depositOpen"
      :currency="currency"
      :balance="balance"
      :default-bank-account="defaultBankAccount"
      :sliceal-account="slicealAccount"
      :submitting="depositSubmitting"
      :submit-error="depositError"
      :submitted-at="depositSubmittedAt"
      :submitted-reference="depositReference"
      @confirm="(payload) => emit('deposit-confirm', payload)"
      @clear-error="emit('deposit-clear-error')"
      @view-history="emit('deposit-view-history')"
    />

    <WithdrawFundsModal
      v-model:visible="withdrawOpen"
      :currency="currency"
      :balance-amount="balanceAmount"
      :accounts="bankAccounts"
      :default-bank-account="defaultBankAccount"
      :submitting="withdrawing"
      @confirm="(payload) => emit('withdraw', payload)"
      @add-bank="emit('add-bank')"
    />
  </Box>
</template>

<style scoped>
.fwt {
  width: 100%;
}
</style>
